import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isApiError, refusalMessage } from '../api';
import { earningsApi } from '../api/earnings';
import { fleetApi } from '../api/fleet';
import { clearDriverSession } from '../lib/driverSession';
import { useFleetRole } from '../lib/fleetRole';
import './driverEarningsWalletProfile.css';
import './driverEarningsPremium.css';

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
];

/** Minor units in the line's own currency ("$8.37"). */
function moneyOf(minor, currency) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format((Number(minor) || 0) / 100);
}

function formatRecentTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (d >= startToday) {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  const yday = new Date(startToday);
  yday.setDate(yday.getDate() - 1);
  if (d >= yday && d < startToday) return 'Yesterday';
  return d.toLocaleDateString(undefined, { dateStyle: 'short' });
}

const HOUR = 3600 * 1000;
const DAY = 24 * HOUR;

/**
 * The period's fares in bars, from the server's lines: today in four-hour
 * blocks, a week a day at a time from the period's own Monday, a month in
 * seven runs of days. Each bar adds the server's numbers; nothing is priced
 * or taken off here. The blocks are counted from the period's own start, an
 * instant in the operator's calendar, never from the phone's clock: a phone
 * set to another zone must not move a delivery to another day.
 *
 * @param {{ key: string, from: string, to: string, zone?: string }} period
 * @param {{ completed_at: string, fare_minor: number }[]} lines
 */
function buildChart(period, lines) {
  const from = new Date(period.from).getTime();
  const sum = (lo, hi) =>
    lines.reduce((s, line) => {
      const t = new Date(line.completed_at).getTime();
      return t >= lo && t < hi ? s + line.fare_minor : s;
    }, 0);

  if (period.key === 'today') {
    return ['12a', '4a', '8a', '12p', '4p', '8p'].map((day, i) => ({ day, minor: sum(from + i * 4 * HOUR, from + (i + 1) * 4 * HOUR) }));
  }

  if (period.key === 'week') {
    return Array.from({ length: 7 }, (_, i) => ({
      // Named at midday, in the operator's zone, so no offset can push it into the next day.
      day: new Date(from + i * DAY + 12 * HOUR).toLocaleDateString('en-GB', { weekday: 'short', timeZone: period.zone || undefined }),
      minor: sum(from + i * DAY, from + (i + 1) * DAY),
    }));
  }

  const days = Math.round((new Date(period.to).getTime() - from) / DAY);
  const chunk = Math.ceil(days / 7);
  return Array.from({ length: 7 }, (_, i) => {
    const fromD = i * chunk + 1;
    const toD = Math.min(days, (i + 1) * chunk);
    return {
      day: fromD > toD ? '' : fromD === toD ? String(fromD) : `${fromD}–${toD}`,
      minor: fromD > toD ? 0 : sum(from + (fromD - 1) * DAY, from + toD * DAY),
    };
  });
}

/** The rate the lines were recorded at: one number when they agree, "varies" when the setting changed between them. */
function rateOf(lines) {
  const rates = [...new Set(lines.map((line) => line.commission_percent))];
  if (rates.length === 0) return null;

  return rates.length === 1 ? `${rates[0]}%` : 'varies';
}

function IcJobsEmpty() {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      style={{ margin: '0 auto 0.75rem', display: 'block', color: '#9ca3af' }}
    >
      <rect x="4" y="5" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 10h8M8 14h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/**
 * What the rider earned, from InGo's API (`rider/me/earnings`, ingo-e0, 2 Oct
 * 2026; InGo ADR 0030): each delivery's fare, the commission recorded on it
 * at completion and what the rider kept, never recomputed at today's rate.
 * The periods are the operator's (a week from Monday), as the office's
 * reports read them.
 *
 * A fleet owner's bar has Fleet where a solo rider's has Wallet, so the
 * owner's wallet opens from here (Rio, 4 Oct 2026: "Wallet inside Earnings").
 */
export default function DriverEarningsPage({ earnings = earningsApi, fleet = fleetApi }) {
  const navigate = useNavigate();
  const isOwner = useFleetRole(fleet) === 'owner';
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadErr('');
    earnings.earnings(period).then(
      (answer) => {
        if (cancelled) return;
        setData(answer);
        setLoading(false);
      },
      (err) => {
        if (cancelled) return;
        if (isApiError(err) && err.status === 401) {
          clearDriverSession();
          navigate('/driver/login', { replace: true });
          return;
        }
        setLoadErr(refusalMessage(err));
        setLoading(false);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [earnings, period, navigate]);

  const lines = useMemo(() => data?.deliveries ?? [], [data]);
  const currency = data?.currency || 'USD';
  const totals = data?.totals ?? { deliveries: 0, fare_minor: 0, commission_minor: 0, earned_minor: 0 };
  const rate = rateOf(lines);
  const sublabel = PERIODS.find((p) => p.id === period)?.label || 'This Week';

  const bars = useMemo(() => {
    if (!data) return [];
    const chart = buildChart(data.period, lines.filter((line) => line.currency === currency));
    const top = Math.max(...chart.map((c) => c.minor), 1);
    return chart.map((c) => ({ ...c, pct: c.minor <= 0 ? 0 : Math.max(9, (c.minor / top) * 100) }));
  }, [data, lines, currency]);

  return (
    <div className="dvRoot dvRoot--premium" role="main">
      <header className="dvH">
        <span className="dv__heroSpacer" aria-hidden />
        <h1>My Earnings</h1>
        <span className="dv__heroSpacer" aria-hidden />
      </header>
      <div className="dvSc">
        {isOwner ? (
          <Link to="/driver/wallet" className="dvWalletLink">
            <span>
              <strong>Wallet</strong>
              <small>Cash you collected and what you owe InGo</small>
            </span>
            <span aria-hidden>›</span>
          </Link>
        ) : null}
        {loadErr ? (
          <p className="dv__notice dv__notice--error" role="alert">
            {loadErr}
          </p>
        ) : null}
        {loading ? <p className="dv__notice">Loading your earnings…</p> : null}

        <section className="dvSum" aria-label="Earnings summary">
          <p className="dvSumL">Fares · {sublabel}</p>
          <p className="dvSumAmt">{moneyOf(totals.fare_minor, currency)}</p>
          <p className="dvSumNet" role="status">
            InGo's commission {moneyOf(totals.commission_minor, currency)} · You keep <span>{moneyOf(totals.earned_minor, currency)}</span>
          </p>
          <div className="dv3r">
            <div className="dv3b">
              <b>{totals.deliveries}</b>
              <span>Completed</span>
            </div>
            <div className="dv3b">
              <b>{rate ?? '—'}</b>
              <span>Commission</span>
            </div>
            <div className="dv3b">
              <b>{moneyOf(totals.deliveries > 0 ? Math.floor(totals.earned_minor / totals.deliveries) : 0, currency)}</b>
              <span>Avg kept / job</span>
            </div>
          </div>
          {data?.other_currencies?.length ? (
            <p className="dvSumNet">Also paid in {data.other_currencies.join(', ')}, not added here.</p>
          ) : null}
        </section>

        <div className="dvTabs" role="tablist" aria-label="Earnings period">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={period === p.id}
              className={period === p.id ? 'dvTab dvTab--on' : 'dvTab'}
              onClick={() => setPeriod(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>

        <section className="dvCh" aria-label="Earnings chart">
          <h2 className="dvChT">Fares</h2>
          <div className="dvBars">
            {bars.map((b, i) => (
              <div className="dvBcol" key={`${b.day}-${i}`}>
                <div className="dvBtrack">
                  <div className={b.minor <= 0 ? 'dvBbar dvBbar--empty' : 'dvBbar'} style={b.minor <= 0 ? { height: '4px' } : { height: `${b.pct}%` }}>
                    {b.minor > 0 ? <span className="dvBval">{moneyOf(b.minor, currency)}</span> : null}
                  </div>
                </div>
                <div className="dvBday">{b.day || '·'}</div>
              </div>
            ))}
          </div>
        </section>

        <h2 className="dvRsec">Completed Jobs</h2>
        {!loading && !loadErr && lines.length === 0 ? (
          <div className="dvJobsEmpty">
            <IcJobsEmpty />
            <p>No deliveries in this period.</p>
          </div>
        ) : null}
        {lines.length > 0 ? (
          <div className="dvJobsCard">
            {lines.map((line) => (
              <div className="dvRrow" key={line.id}>
                <div>
                  <p className="dvRid">
                    #{line.reference} <span className="dvRkind">(Parcel)</span>
                  </p>
                  <p className="dvRto">
                    {line.pickup} → {line.dropoff}
                  </p>
                </div>
                <div className="dvRrowMeta">
                  <span className="dvRsub">{formatRecentTime(line.completed_at)}</span>
                  <span className="dvRAmt">{moneyOf(line.fare_minor, line.currency)} fare</span>
                  <span className="dvRnet">
                    You keep {moneyOf(line.earned_minor, line.currency)} ({line.commission_percent}% commission)
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
